import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import SitePage, { CallToAction, calendly, styles, T, ACCENT } from '../components/site/SitePage';

const PATH = '/formation-ia-equipe-ecommerce';

const PROGRAMME = [
  ['30 min', 'Vos questions, pas une démo', "On part des questions que l'équipe se pose vraiment : quelle marge sur ce produit, quoi commander, quelle campagne couper."],
  ['30 min', 'Préparer les données', "Les exports ou la base que l'IA va lire, rangés pour qu'elle réponde juste. Je les prépare avec vous avant la séance."],
  ['1 h', 'Atelier sur vos chiffres', 'Chacun pose ses questions à Claude ou ChatGPT sur vos propres données : marge, stock, publicité, catalogue.'],
  ['30 min', 'Vérifier une réponse', 'Les pièges qui coûtent cher : un chiffre inventé, une mauvaise période, un total qui ne colle pas. Et comment les repérer en une minute.'],
  ['20 min', 'Les règles du jeu', 'Ce qui peut sortir de l’entreprise et ce qui ne sort pas, les comptes professionnels, les données sensibles.'],
  ['40 min', 'Former le référent', "Une personne de l'équipe repart avec la méthode pour accompagner les autres au quotidien."]
];

const GARDE = [
  ['Un guide d’une page', 'Les bonnes questions à poser, et comment les formuler.'],
  ['Vos requêtes types', 'Celles de l’atelier, prêtes à réutiliser chaque semaine.'],
  ['Une liste de vérification', 'Pour savoir si une réponse de l’IA tient debout.'],
  ['Un référent formé', 'Qui prend le relais, pour ne pas dépendre de moi.']
];

const FAQ = [
  ['Faut-il savoir coder ?', "Non. La formation s'adresse aux équipes achats, finance, marketing et direction. On travaille dans Claude ou ChatGPT, avec vos fichiers."],
  ['Claude ou ChatGPT ?', "Les deux fonctionnent. On choisit ensemble selon ce que vous utilisez déjà. Je recommande l'offre professionnelle : vos données n'y servent pas à entraîner les modèles."],
  ['Nos données sont-elles en sécurité ?', "On n'utilise que des comptes professionnels, et on décide ensemble de ce qui peut être partagé avec l'IA. Avant la séance, on signe un accord de confidentialité."],
  ['Est-ce finançable par un OPCO ?', "Non : je ne suis pas certifié Qualiopi. La formation est facturée comme une prestation, 900 € HT la demi-journée."],
  ["Et si nous n'avons pas de base de données ?", "On travaille sur vos exports et vos Excel. Si l'atelier montre qu'il manque une base unique pour que l'IA réponde juste, on en parle : c'est l'offre « Votre système », dans laquelle la formation est incluse."]
];

const FAQ_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } }))
};

const SERVICE_LD = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Formation IA pour les équipes e-commerce',
  serviceType: 'Formation',
  description: "Une demi-journée pour apprendre à une équipe e-commerce à interroger ses données avec Claude ou ChatGPT, à vérifier les réponses, et former un référent.",
  provider: { '@type': 'ProfessionalService', name: 'Autoria', url: 'https://autoria.dev' },
  areaServed: ['France', 'Belgique', 'Suisse'],
  offers: { '@type': 'Offer', price: '900', priceCurrency: 'EUR' }
};

const FormationPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SitePage>
      <SEO
        title="Formation IA pour les équipes e-commerce (ChatGPT, Claude)"
        description="Une demi-journée pour apprendre à votre équipe à interroger ses chiffres avec Claude ou ChatGPT, vérifier les réponses et former un référent. 900 € HT."
        path={PATH}
        breadcrumb={[['Formation IA', PATH]]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(SERVICE_LD)}</script>
      </Helmet>

      <section style={{ ...styles.container, padding: '56px 24px 0', display: 'flex', flexWrap: 'wrap', gap: 40, alignItems: 'center' }}>
        <div style={{ flex: '1 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <p style={styles.kicker}>Formation · une demi-journée</p>
          <h1 style={styles.h1}>Former votre équipe e-commerce à l'IA, sur vos propres données</h1>
          <p style={styles.lead}>Une demi-journée de montée en compétences sur les outils d'IA : votre équipe apprend à interroger ses chiffres avec Claude ou ChatGPT, à vérifier les réponses, et un référent prend le relais.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <a href={calendly('formation-page')} target="_blank" rel="noopener" className="lift" style={styles.button}>Réserver un appel de 30 min</a>
            <a href="#programme" className="lift" style={{ ...styles.button, background: '#FFFFFF', color: T.ink, border: `1px solid ${T.ink}`, fontWeight: 600 }}>Voir le programme</a>
          </div>
        </div>
        <div style={{ flex: '1 1 320px', minWidth: 0, background: '#FFFFFF', border: `2px solid ${ACCENT}`, borderRadius: 10, padding: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <span style={{ fontFamily: T.mono, fontSize: 13, color: T.faint }}>Formation de votre équipe</span>
          <span style={{ fontSize: 44, fontWeight: 800, fontStretch: '80%', lineHeight: 1 }}>900 € <span style={{ fontSize: 16, color: T.muted, fontWeight: 600 }}>HT la demi-journée</span></span>
          <ul style={{ margin: '6px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 15, color: T.text }}>
            <li>— En visio, avec votre équipe</li>
            <li>— Sur vos données, préparées avec vous avant</li>
            <li>— Avec Claude ou ChatGPT, en compte professionnel</li>
            <li>— Incluse dans l'offre « Votre système »</li>
          </ul>
        </div>
      </section>

      <section style={{ ...styles.container, paddingTop: 88 }}>
        <h2 style={styles.h2}>Pour qui</h2>
        <p style={{ margin: '16px 0 0', fontSize: 18, color: T.text, lineHeight: 1.6, maxWidth: 760 }}>Les équipes d'une marque e-commerce qui passent du temps à chercher des chiffres : achats, finance, marketing, direction. Aucune compétence technique n'est demandée. L'objectif n'est pas de « découvrir l'IA », mais que chacun sache, en sortant, lui poser une vraie question de son métier et savoir si la réponse est juste.</p>
      </section>

      <section id="programme" style={{ ...styles.container, paddingTop: 88, scrollMarginTop: 84 }}>
        <h2 style={styles.h2}>Le programme</h2>
        <ol style={{ margin: '28px 0 0', padding: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: 1, background: T.line2, border: `1px solid ${T.line2}` }}>
          {PROGRAMME.map(([duree, titre, texte], i) => (
            <li key={titre} style={{ padding: 22, background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <span style={{ fontFamily: T.mono, fontSize: 13, color: ACCENT }}>0{i + 1} · {duree}</span>
              <p style={{ margin: 0, fontSize: 19, fontWeight: 800, fontStretch: '88%' }}>{titre}</p>
              <p style={{ margin: 0, fontSize: 15, color: T.muted }}>{texte}</p>
            </li>
          ))}
        </ol>
      </section>

      <section style={{ ...styles.container, paddingTop: 88 }}>
        <h2 style={styles.h2}>Ce que l'équipe garde</h2>
        <div style={{ marginTop: 28, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(230px, 100%), 1fr))', gap: 28 }}>
          {GARDE.map(([titre, texte]) => (
            <div key={titre} style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: `2px solid ${ACCENT}`, paddingTop: 14 }}>
              <p style={{ margin: 0, fontSize: 19, fontWeight: 800, fontStretch: '88%' }}>{titre}</p>
              <p style={{ margin: 0, fontSize: 16, color: T.text }}>{texte}</p>
            </div>
          ))}
        </div>
      </section>

      <section style={{ ...styles.narrow, paddingTop: 88 }}>
        <h2 style={styles.h2}>Avant la séance</h2>
        <ul style={{ margin: '16px 0 0', paddingLeft: 20, listStyle: 'disc', fontSize: 17, color: T.text, lineHeight: 1.7 }}>
          <li>un appel de 30 minutes pour lister les questions de l'équipe ;</li>
          <li>vos exports ou l'accès à votre base, que je prépare pour l'atelier ;</li>
          <li>un compte Claude ou ChatGPT par participant, de préférence en offre professionnelle.</li>
        </ul>

        <h2 id="faq" style={{ ...styles.h2, marginTop: 56 }}>Questions fréquentes</h2>
        <div style={{ marginTop: 20, borderTop: `1px solid ${T.line2}` }}>
          {FAQ.map(([q, a]) => (
            <details key={q} style={{ borderBottom: `1px solid ${T.line2}`, padding: '16px 0' }}>
              <summary style={{ cursor: 'pointer', fontSize: 18, fontWeight: 700 }}>{q}</summary>
              <p style={{ margin: '10px 0 0', fontSize: 17, color: T.text, lineHeight: 1.6 }}>{a}</p>
            </details>
          ))}
        </div>
      </section>

      <CallToAction from="formation-bas" title="Former votre équipe ?" text="30 minutes pour lister les questions de votre équipe, et voir si la demi-journée vaut le coup." />
    </SitePage>
  );
};

export default FormationPage;
